import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const categories = [
  { id: 'system', label: 'システム開発', icon: 'ri-server-line' },
  { id: 'app', label: 'アプリ開発', icon: 'ri-smartphone-line' },
  { id: 'web', label: 'Webサービス', icon: 'ri-global-line' },
];

const plans: Record<string, { name: string; price: string; desc: string; features: string[]; recommended?: boolean }[]> = {
  system: [
    {
      name: 'スモール',
      price: '¥500,000〜',
      desc: '小規模業務システム・社内ツール向け',
      features: ['要件定義・設計', '開発期間 1〜2ヶ月', 'テスト・納品', '3ヶ月保守サポート'],
    },
    {
      name: 'スタンダード',
      price: '¥1,500,000〜',
      desc: '中規模業務システム・基幹システム向け',
      features: ['要件定義・設計', 'クラウド構築対応', '開発期間 3〜6ヶ月', '6ヶ月保守サポート', 'ユーザー研修'],
      recommended: true,
    },
    {
      name: 'エンタープライズ',
      price: '要お見積もり',
      desc: '大規模・複雑なシステム開発',
      features: ['フルカスタム設計', 'セキュリティ強化', '専任担当者アサイン', '12ヶ月保守サポート', '24時間対応'],
    },
  ],
  app: [
    {
      name: 'シンプル',
      price: '¥800,000〜',
      desc: 'シンプルなアプリ・MVP開発向け',
      features: ['iOS or Android', 'UI/UXデザイン', '開発期間 2〜3ヶ月', 'ストア申請サポート'],
    },
    {
      name: 'スタンダード',
      price: '¥2,000,000〜',
      desc: 'iOS・Android両対応アプリ',
      features: ['iOS & Android対応', 'UI/UXデザイン', 'API連携', '開発期間 3〜5ヶ月', 'ストア申請サポート'],
      recommended: true,
    },
    {
      name: 'フルカスタム',
      price: '要お見積もり',
      desc: '複雑な機能・大規模アプリ',
      features: ['フルカスタム開発', '独自バックエンド構築', 'プッシュ通知・決済連携', '専任チーム対応', '長期保守サポート'],
    },
  ],
  web: [
    {
      name: 'ライト',
      price: '月額 ¥30,000〜',
      desc: 'Webサービス立ち上げ・小規模運営',
      features: ['Webサービス開発', 'SEO基本対応', '月次レポート', 'メールサポート'],
    },
    {
      name: 'スタンダード',
      price: '月額 ¥80,000〜',
      desc: 'Webサービス運営・広告運用',
      features: ['Webサービス開発・運営', 'インターネット広告運用', 'SEO・グロース支援', '週次レポート', '電話・メールサポート'],
      recommended: true,
    },
    {
      name: 'プレミアム',
      price: '月額 ¥200,000〜',
      desc: '本格的なWebサービス・広告代理',
      features: ['フルサービス開発・運営', '広告代理業対応', 'マーケティング戦略立案', '専任担当者', '24時間サポート'],
    },
  ],
};

export default function PlansPage() {
  const [activeCategory, setActiveCategory] = useState('system');
  const currentPlans = plans[activeCategory];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-900 to-cyan-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20turquoise%20ocean%20wave%20pattern%20digital%20technology%20background%20minimal%20clean%20teal%20cyan%20gradient%20with%20geometric%20shapes&width=1920&height=400&seq=ocean-plans-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-teal-200 text-xs font-semibold uppercase tracking-widest mb-3">Pricing</p>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            料金プラン
          </h1>
          <p className="text-white/70 text-sm max-w-xl mx-auto">
            サービス種別ごとに最適なプランをご用意しています。<br className="hidden md:block" />
            まずはお気軽にご相談ください。
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'border-teal-400 bg-teal-50 text-teal-700'
                    : 'border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {currentPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl border-2 p-6 flex flex-col ${
                  plan.recommended ? 'border-teal-400 bg-teal-50/30' : 'border-gray-100 bg-white'
                }`}
              >
                {plan.recommended && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-teal-400 to-cyan-500 text-white text-xs font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                    おすすめ
                  </span>
                )}
                <h3 className="text-base font-bold text-gray-900 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {plan.name}
                </h3>
                <p className="text-gray-500 text-xs mb-3">{plan.desc}</p>
                <div className="text-2xl font-bold text-teal-600 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {plan.price}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-teal-500 text-xs"></i>
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center py-2.5 rounded-full text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                    plan.recommended
                      ? 'bg-gradient-to-r from-teal-400 to-cyan-500 text-white hover:opacity-90'
                      : 'border border-teal-400 text-teal-600 hover:bg-teal-50'
                  }`}
                >
                  相談する
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-400 text-xs mt-8">
            ※ 表示価格は目安です。要件により変動します。詳細はお問い合わせください。
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
